import React, { useContext, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { common, FilterCollection } from 'plato-js-client';
import { Button, Card, CardBody, CardFooter, CardHeader, Table } from 'reactstrap';
import { addTab } from '../Actions';
import { Store } from '../Store';
import Icon from '../components/Icon';
import { globalChecklistsQueryOptions, propertyChecklistsQueryOptions } from '../checklists/checklistQueries';

/**
 * @param {(checklist: common.PmsChecklistGlobal) => void} onChecklistSelect
 * @param {string[]} extendedChecklistIds
 * @returns {React.JSX.Element}
 * @constructor
 */
function GlobalChecklistSelect({
    onChecklistSelect,
    extendedChecklistIds,
}) {
    // Create a property-level override of the global checklist then, once we have a checklistId,
    // fire the event to select and start editing it.
    const [ selectedChecklistId, setSelectedChecklistId ] = useState(null);

    const {
        data: globalChecklists,
        isFetching,
    } = useQuery(globalChecklistsQueryOptions());
    const globalChecklistsAvailableToCustomise = globalChecklists.filter(checklist => !extendedChecklistIds.includes(checklist.id));

    if (globalChecklistsAvailableToCustomise.length < 1) {
        return (
            <Card>
                <CardBody>
                    { isFetching ? 'Loading...' : 'No more global checklists available to select.' }
                </CardBody>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader>
                Select a global checklist to customise
            </CardHeader>
            <CardBody>
                <select
                    defaultValue={ '' }
                    onChange={ (e) => {
                        const value = e.currentTarget.value;
                        if ('' === value) {
                            return setSelectedChecklistId(null);
                        }

                        setSelectedChecklistId(String(value));
                    } }
                    disabled={ isFetching }
                >
                    <option value="">Select a global checklist</option>
                    { globalChecklistsAvailableToCustomise.map(checklist => (
                        <option
                            key={ checklist.id }
                            value={ checklist.id }
                        >
                            { checklist.name }
                        </option>
                    )) }
                </select>
            </CardBody>
            <CardFooter>
                <Button
                    disabled={ selectedChecklistId === null }
                    onClick={ () => onChecklistSelect(globalChecklists.find(checklist => String(checklist.id) === selectedChecklistId)) }
                >Customise</Button>
            </CardFooter>
        </Card>
    );
}

/**
 * @param {(checklist: common.PmsChecklistProperty) => void} onChecklistSelect
 * @param {common.PmsChecklistProperty[]} propertyChecklists
 * @returns {React.JSX.Element}
 * @constructor
 */
function PropertyLevelChecklists({
    propertyChecklists,
    onChecklistEdit,
}) {
    return (
        <Card>
            <CardHeader>Property-level Checklists Customisation</CardHeader>
            <CardBody>
                <Table striped>
                    <thead>
                    <tr>
                        <th>Name</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tfoot></tfoot>
                    <tbody>
                    { propertyChecklists.length < 1 ? (
                        <tr>
                            <td colSpan={ 2 }>
                                <p>
                                    This property doesn&apos;t define any custom checklists yet.<br />
                                    Use the dropdown below to select a global checklist to customise.
                                </p>
                            </td>
                        </tr>
                    ) : null }
                    { propertyChecklists.map((checklist) => (
                        <tr key={ checklist.id }>
                            <td>
                                { checklist.name }
                            </td>
                            <td>
                                <Icon
                                    icon="pencil-alt"
                                    onClick={ () => onChecklistEdit(checklist) }
                                />
                            </td>
                        </tr>
                    )) }
                    </tbody>
                </Table>
            </CardBody>
        </Card>
    );
}

/**
 * @param {common.PmsChecklistProperty} propertyChecklist
 * @param {string} propertyId
 * @return string
 */
function tabName(propertyChecklist, propertyId) {
    return `${ propertyChecklist.name } (Property ${ propertyId })`;
}

/**
 * @param {string} propertyId
 * @param {common.PmsChecklistGlobal} globalChecklist
 * @returns {Promise<*>}
 */
async function fetchOrCreatePropertyChecklist(propertyId, globalChecklist) {
    const propertyChecklists = new FilterCollection({
        path: 'pmschecklist/property',
        object: common.PmsChecklistProperty,
    });

    propertyChecklists.addFilter('propertyid', propertyId);

    await propertyChecklists.fetch();

    // TODO: Maybe add a backend "globalchecklistid" filter that saves us from iterating through all property-level checklists?
    const propertyChecklistFound = propertyChecklists.collection.find(checklist => checklist.extendsGlobalChecklist(globalChecklist.id));
    if (propertyChecklistFound) {
        return propertyChecklistFound;
    }

    const propertyChecklistData = new common.PmsChecklistProperty();
    propertyChecklistData.name = globalChecklist.name;
    propertyChecklistData.property = new common.Property(propertyId);
    propertyChecklistData.pmschecklistglobal = globalChecklist;

    try {
        return await propertyChecklistData.create();
    } catch (e) {
        console.error(`Failed to create a property checklist from the global ${ globalChecklist.id }`, e);
    }
}

export function TabChecklists({ propertyId }) {
    const { dispatch } = useContext(Store);
    const queryClient = useQueryClient();
    const { data: propertyChecklists } = useQuery(propertyChecklistsQueryOptions(propertyId));
    const extendedChecklistIds = propertyChecklists.map(checklist => checklist.pmschecklistglobal.id);

    const createPropertyChecklist = (globalChecklist) => {
        fetchOrCreatePropertyChecklist(propertyId, globalChecklist)
            .then((propertyChecklist) => {
                queryClient.setQueryData([ 'checklists', { propertyId } ], (data) => {
                    return [
                        ...data,
                        propertyChecklist,
                    ];
                });
                return propertyChecklist;
            })
            .then((propertyChecklist) => addTab('editPropertyChecklist', {
                propertyId,
                propertyChecklistId: propertyChecklist.id,
                label: tabName(propertyChecklist, propertyId),
            }, dispatch));
    };

    return (
        <div
            style={ {
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem ',
            } }
        >
            <PropertyLevelChecklists
                propertyChecklists={ propertyChecklists }
                onChecklistEdit={ (propertyChecklist) => addTab('editPropertyChecklist', {
                    propertyId,
                    propertyChecklistId: propertyChecklist.id,
                    label: tabName(propertyChecklist, propertyId),
                }, dispatch) }
            />

            <GlobalChecklistSelect
                extendedChecklistIds={ extendedChecklistIds }
                onChecklistSelect={ (globalChecklist) => createPropertyChecklist(globalChecklist) }
            />
        </div>
    );
}
